<template>
    <div class="document-compliance-review">
        <BAlert
            v-if="violations.length"
            variant="danger"
            show
        >
            <div class="alert-body">
                <strong>{{ translate('Component(ReviewProcess):validation-failed', 'Some information needs attention') }}</strong>
                <ul class="mb-0 mt-50 pl-1">
                    <li
                        v-for="(violation, index) in violations"
                        :key="`${violation.path || 'violation'}-${index}`"
                    >
                        {{ violation.message }}
                    </li>
                </ul>
            </div>
        </BAlert>

        <component
            :is="layoutComponent"
            :show-document-preview="isSectionVisible('documentPreview')"
        >
            <template #documentPreview>
                <component
                    v-if="isSectionVisible('documentPreview')"
                    :is="documentPreviewSectionComponent"
                    :file-reference-id="fileReferenceId"
                    :workspace-service="workspaceService"
                />
            </template>

            <template #heading>
                <div class="document-compliance-review__heading mb-1">
                    <div>
                        <h4 v-if="showDocumentTitle" class="mb-25">
                            {{ documentTitle }}
                        </h4>
                        <p class="text-less-muted mb-0">
                            {{ translate(
                                'Component(ResearchReviewProcess):review-document-description',
                                'Review the extracted information and correct it when needed.',
                            ) }}
                        </p>
                    </div>
                    <BBadge :variant="item.status === 'reviewed' ? 'light-success' : 'light-warning'">
                        {{ translatedItemStatus }}
                    </BBadge>
                </div>
            </template>

            <template #documentInformation>
                <component
                    v-if="isSectionVisible('documentInformation')"
                    :is="documentInformationSectionComponent"
                    :classification="draft.classification"
                    :document-information="draft.documentInformation"
                    :suggestions="suggestions"
                    :show-unknown-scope="getSectionOption(
                        'documentInformation',
                        'showUnknownScope',
                        true,
                    )"
                    :show-relevance="!relevancePresentationComponent"
                    @update:classification="updateDraftSection('classification', $event)"
                    @update:document-information="updateDraftSection('documentInformation', $event)"
                />
            </template>

            <template #regulations>
                <component
                    v-if="isSectionVisible('regulations')"
                    :is="regulationsSectionComponent"
                    :legislations="draft.legislations"
                    :available-legislations="reviewContext.legislations || []"
                    :exemptions="draft.exemptions"
                    :claim-editor="rendererOptions.claimEditor || 'structured'"
                    :related-substances-projection="substanceRegulationProjection"
                    :resolving-related-substances="resolvingSubstanceRegulations"
                    :related-substances-error="substanceRegulationsFailed"
                    :substances="extractedSubstances"
                    :substance-links="draft.substanceLinks"
                    :missing-regulation-substance-ids="missingRegulationSubstanceIds"
                    :workspace-service="workspaceService"
                    :show-description="getSectionOption('regulations', 'showDescription', true)"
                    :show-tab-substance-count="getSectionOption(
                        'regulations',
                        'showTabSubstanceCount',
                        false,
                    )"
                    :show-tab-attention="getSectionOption(
                        'regulations',
                        'showTabAttention',
                        false,
                    )"
                    :hide-empty-negative-substances="getSectionOption(
                        'regulations',
                        'hideEmptyNegativeSubstances',
                        false,
                    )"
                    :hide-substances-until-declaration="getSectionOption(
                        'regulations',
                        'hideSubstancesUntilDeclaration',
                        false,
                    )"
                    :allow-remove-regulation="getSectionOption(
                        'regulations',
                        'allowRemoveRegulation',
                        false,
                    )"
                    :ensure-scip-regulation-for-number="getSectionOption(
                        'regulations',
                        'ensureScipRegulationForNumber',
                        false,
                    )"
                    :substance-declaration-inference="getSectionOption(
                        'regulations',
                        'substanceDeclarationInference',
                        {},
                    )"
                    :content="getSectionOption('regulations', 'content', {})"
                    :visibility-policies="getSectionOption(
                        'regulations',
                        'visibilityPolicies',
                        [],
                    )"
                    :scip-information="draft.scipInformation || null"
                    :violations="violations"
                    @update="updateRegulations"
                    @update-scip-information="updateDraftSection('scipInformation', $event)"
                    @initialization-start="beginFormInitialization"
                    @initialization-end="endFormInitialization"
                    @completion-validity-change="setRegulationsCompletionValidity"
                    @completion-issues-change="setRegulationsCompletionIssues"
                />
            </template>

            <template #scipInformation>
                <component
                    v-if="isSectionVisible('scipInformation')"
                    :is="scipInformationSectionComponent"
                    :value="draft.scipInformation || null"
                    :violations="violations"
                    @input="updateDraftSection('scipInformation', $event)"
                />
            </template>

            <template #substances>
                <component
                    v-if="isSectionVisible('substances')"
                    :is="substancesSectionComponent"
                    :substances="extractedSubstances"
                    :links="draft.substanceLinks"
                    :missing-regulation-substance-ids="missingRegulationSubstanceIds"
                    :workspace-service="workspaceService"
                    :violations="violations"
                    @update="updateSubstances"
                />
            </template>

            <template #products>
                <component
                    v-if="isSectionVisible('products')"
                    :is="productsSectionComponent"
                    :products="draft.products"
                    :links="draft.productLinks"
                    :violations="violations"
                    :review-context="reviewContext"
                    :workspace-service="workspaceService"
                    :allow-add-product="getSectionOption('products', 'allowAddProduct', true)"
                    :allow-add-attribute="getSectionOption('products', 'allowAddAttribute', true)"
                    @update="updateProducts"
                />
            </template>

            <template #completion>
                <component
                    :is="relevancePresentationComponent"
                    v-if="relevancePresentationComponent"
                    :value="draft.documentInformation.relevance || 'uncertain'"
                    @input="updateDocumentRelevance"
                />
            </template>
        </component>
    </div>
</template>

<script>
import {
    BAlert,
    BBadge,
} from 'bootstrap-vue'
import { resolveDocumentComplianceLayout } from './layout-registry'
import {
    DocumentComplianceSection,
    resolveDocumentComplianceSection,
} from './section-registry'
import { createDocumentComplianceCompositionContext } from './composition-context'
import { evaluateDocumentComplianceSectionCondition } from './section-condition'
import {
    isDocumentRelevanceRequiredForCompletion,
    resolveDocumentRelevancePresentation,
} from './relevance/document-relevance-presentation-registry'
import {
    buildSubstanceRegulationProjection,
    getAcceptedSubstanceIds,
} from './regulations/substance-regulation-projection'

const clone = value => JSON.parse(JSON.stringify(value || {}))

export default {
    name: 'DocumentComplianceReview',
    components: {
        BAlert,
        BBadge,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        item: {
            type: Object,
            required: true,
        },
        violations: {
            type: Array,
            default: () => [],
        },
        suggestions: {
            type: Array,
            default: () => [],
        },
        reviewContext: {
            type: Object,
            default: () => ({}),
        },
        workspaceService: {
            type: Object,
            required: true,
        },
        rendererOptions: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        draft() {
            return this.value
        },
        sectionOptions() {
            return this.rendererOptions.sections || {}
        },
        showDocumentTitle() {
            const heading = this.rendererOptions.heading

            return !heading || heading.showTitle !== false
        },
        initializationOverlayEnabled() {
            const initialization = this.rendererOptions.initialization

            return !!(initialization && initialization.showOverlay === true)
        },
        relevanceConfig() {
            return this.rendererOptions.relevance || {}
        },
        relevancePresentationComponent() {
            return resolveDocumentRelevancePresentation(this.relevanceConfig)
        },
        relevanceRequiredForCompletion() {
            return isDocumentRelevanceRequiredForCompletion(this.relevanceConfig)
        },
        relevanceCompletionValid() {
            return !this.relevanceRequiredForCompletion
                || ['relevant', 'irrelevant'].includes(this.draft.documentInformation.relevance)
        },
        documentIsIrrelevant() {
            return this.draft.documentInformation.relevance === 'irrelevant'
        },
        completionValid() {
            const regulationsValid = !this.isSectionVisible('regulations')
                || this.regulationsCompletionValid

            return this.relevanceCompletionValid
                && (this.documentIsIrrelevant || regulationsValid)
        },
        completionIssues() {
            const issues = this.documentIsIrrelevant
                ? []
                : this.regulationsCompletionIssues.slice()
            if (!this.relevanceCompletionValid) {
                issues.unshift({
                    id: 'document-relevance',
                    reason: {
                        messageKey: 'Component(ResearchReviewProcess):reason-select-document-relevance',
                        fallbackMessage: 'Choose whether the document is relevant before completing the review.',
                        params: {},
                    },
                })
            }

            return issues
        },
        compositionContext() {
            return createDocumentComplianceCompositionContext({
                draft: this.draft,
                item: this.item,
                reviewContext: this.reviewContext,
            })
        },
        layoutComponent() {
            return resolveDocumentComplianceLayout(this.rendererOptions.layout)
        },
        documentPreviewSectionComponent() {
            return resolveDocumentComplianceSection(
                DocumentComplianceSection.DOCUMENT_PREVIEW,
                this.getSectionVariant('documentPreview'),
            )
        },
        documentInformationSectionComponent() {
            return resolveDocumentComplianceSection(
                DocumentComplianceSection.DOCUMENT_INFORMATION,
                this.getSectionVariant('documentInformation'),
            )
        },
        regulationsSectionComponent() {
            return resolveDocumentComplianceSection(
                DocumentComplianceSection.REGULATIONS,
                this.getSectionVariant('regulations'),
            )
        },
        scipInformationSectionComponent() {
            return resolveDocumentComplianceSection(
                DocumentComplianceSection.SCIP_INFORMATION,
                this.getSectionVariant('scipInformation'),
            )
        },
        substancesSectionComponent() {
            return resolveDocumentComplianceSection(
                DocumentComplianceSection.SUBSTANCES,
                this.getSectionVariant('substances'),
            )
        },
        productsSectionComponent() {
            return resolveDocumentComplianceSection(
                DocumentComplianceSection.PRODUCTS,
                this.getSectionVariant('products'),
            )
        },
        fileReferenceId() {
            if (this.draft.fileReferenceId) {
                return this.draft.fileReferenceId
            }

            return this.item && this.item.metadata ? this.item.metadata.fileReferenceId || null : null
        },
        documentTitle() {
            return this.draft.documentInformation.title
                || this.translate('Component(ReviewProcess):source-document', 'Source document')
        },
        translatedItemStatus() {
            const status = this.item.status || 'pending'

            return this.translate(
                `Component(ResearchReviewProcessEnum):item-status-${status}`,
                status,
            )
        },
        extractedSubstances() {
            const assessment = this.draft.substanceAssessment

            return assessment && Array.isArray(assessment.substances)
                ? assessment.substances
                : []
        },
        acceptedSubstanceIds() {
            return getAcceptedSubstanceIds(this.draft.substanceLinks)
        },
        acceptedSubstanceIdsKey() {
            return this.isSectionVisible('regulations')
                ? this.acceptedSubstanceIds.join(',')
                : null
        },
        substanceRegulationProjection() {
            return buildSubstanceRegulationProjection({
                substances: this.extractedSubstances,
                links: this.draft.substanceLinks,
                resolutionItems: this.substanceRegulationItems,
            })
        },
    },
    data() {
        return {
            substanceRegulationItems: [],
            missingRegulationSubstanceIds: [],
            resolvingSubstanceRegulations: false,
            substanceRegulationsFailed: false,
            substanceRegulationRevision: 0,
            regulationsCompletionValid: true,
            regulationsCompletionIssues: [],
            initializationRegistrationOpen: !!(
                this.rendererOptions.initialization
                && this.rendererOptions.initialization.showOverlay === true
            ),
            initializationPendingTasks: {},
            initializationComplete: false,
        }
    },
    created() {
        this.$emit('initialization-change', this.initializationOverlayEnabled)
    },
    mounted() {
        this.$nextTick(() => {
            this.initializationRegistrationOpen = false
            this.finishFormInitialization()
        })
    },
    watch: {
        acceptedSubstanceIdsKey: {
            immediate: true,
            handler() {
                this.resolveSubstanceRegulations()
            },
        },
        completionValid: {
            immediate: true,
            handler(value) {
                this.$emit('completion-validity-change', value)
            },
        },
        completionIssues: {
            deep: true,
            immediate: true,
            handler(value) {
                this.$emit('completion-issues-change', clone(value))
            },
        },
    },
    beforeDestroy() {
        this.substanceRegulationRevision += 1
        this.$emit('initialization-change', false)
    },
    methods: {
        isSectionVisible(section) {
            const config = this.sectionOptions[section]

            if (!config || typeof config === 'string') {
                return true
            }

            if (config.visible === false) {
                return false
            }

            return !config.visibleWhen || evaluateDocumentComplianceSectionCondition(
                config.visibleWhen,
                this.compositionContext,
            )
        },
        getSectionVariant(section) {
            const config = this.sectionOptions[section]

            if (typeof config === 'string') {
                return config
            }

            return config && config.variant ? config.variant : null
        },
        getSectionOption(section, option, fallback = null) {
            const config = this.sectionOptions[section]

            return config && typeof config === 'object' && Object.prototype.hasOwnProperty.call(
                config,
                option,
            ) ? config[option] : fallback
        },
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        beginFormInitialization(task) {
            if (!this.initializationOverlayEnabled || !this.initializationRegistrationOpen || !task) {
                return
            }

            this.$set(this.initializationPendingTasks, task, true)
        },
        endFormInitialization(task) {
            if (task) {
                this.$delete(this.initializationPendingTasks, task)
            }
            this.finishFormInitialization()
        },
        finishFormInitialization() {
            if (!this.initializationOverlayEnabled
                || this.initializationComplete
                || this.initializationRegistrationOpen
                || Object.keys(this.initializationPendingTasks).length) {
                return
            }

            this.initializationComplete = true
            this.$emit('initialization-change', false)
        },
        updateDraftSection(key, value) {
            this.$emit('input', {
                ...clone(this.draft),
                [key]: clone(value),
            })
        },
        updateDocumentRelevance(value) {
            this.updateDraftSection('documentInformation', {
                ...this.draft.documentInformation,
                relevance: value,
            })
        },
        setRegulationsCompletionValidity(value) {
            this.regulationsCompletionValid = value !== false
        },
        setRegulationsCompletionIssues(value) {
            this.regulationsCompletionIssues = Array.isArray(value) ? clone(value) : []
        },
        updateDraftSections(sections) {
            this.$emit('input', {
                ...clone(this.draft),
                ...clone(sections),
            })
        },
        updateSubstances({ substances, links }) {
            this.updateDraftSections({
                substanceAssessment: {
                    ...(clone(this.draft.substanceAssessment || {})),
                    substances: clone(substances),
                },
                substanceLinks: clone(links),
            })
        },
        updateRegulations(value) {
            const updates = clone(value || {})
            const hasSubstances = Object.prototype.hasOwnProperty.call(updates, 'substances')
            const hasLinks = Object.prototype.hasOwnProperty.call(updates, 'links')

            if (hasSubstances) {
                updates.substanceAssessment = {
                    ...clone(this.draft.substanceAssessment || {}),
                    substances: clone(updates.substances),
                }
                delete updates.substances
            }
            if (hasLinks) {
                updates.substanceLinks = clone(updates.links)
                delete updates.links
            }

            this.updateDraftSections(updates)
        },
        updateProducts({ products, links }) {
            this.updateDraftSections({
                products: clone(products),
                productLinks: clone(links),
            })
        },
        resolveSubstanceRegulations() {
            this.substanceRegulationRevision += 1
            const revision = this.substanceRegulationRevision
            const initializationTask = `substance-regulations-${revision}`
            this.substanceRegulationsFailed = false

            if (!this.isSectionVisible('regulations') || !this.acceptedSubstanceIds.length) {
                this.substanceRegulationItems = []
                this.missingRegulationSubstanceIds = []
                this.resolvingSubstanceRegulations = false
                return Promise.resolve()
            }

            this.beginFormInitialization(initializationTask)
            this.resolvingSubstanceRegulations = true
            return this.workspaceService.resolveSubstanceRegulations(this.acceptedSubstanceIds)
                .then(result => {
                    if (revision !== this.substanceRegulationRevision) {
                        return
                    }

                    this.substanceRegulationItems = Array.isArray(result.items) ? result.items : []
                    this.missingRegulationSubstanceIds = Array.isArray(result.missingSubstanceIds)
                        ? result.missingSubstanceIds
                        : []
                })
                .catch(() => {
                    if (revision !== this.substanceRegulationRevision) {
                        return
                    }

                    this.substanceRegulationItems = []
                    this.missingRegulationSubstanceIds = []
                    this.substanceRegulationsFailed = true
                })
                .finally(() => {
                    if (revision === this.substanceRegulationRevision) {
                        this.resolvingSubstanceRegulations = false
                    }
                    this.endFormInitialization(initializationTask)
                })
        },
    },
}
</script>

<style lang="scss">
.document-compliance-review {
    --review-secondary-text-color: #45434c;

    .text-less-muted,
    .text-muted {
        color: var(--review-secondary-text-color) !important;
    }

    .review-section-description {
        font-size: .9rem;
    }
}
</style>

<style scoped>
.document-compliance-review__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

</style>
